// How much a skill on the community is used (migration 109). One row per
// listed skill, keyed by the listing's own key. A use is a copy, a save, a pull
// through the door, or an open — counted, never attributed to a person.
//
// `score` is what the "top" view sorts by: a save is worth the most (someone
// kept it), then a pull (an ai used it), then a copy, then a look.

import type { SupabaseClient } from "@supabase/supabase-js";

export type UseKind = "copy" | "save" | "pull" | "open";
export const USE_KINDS: UseKind[] = ["copy", "save", "pull", "open"];

export interface SkillUses { copies: number; saves: number; pulls: number; opens: number }

const KEY = /^(innernet|commons|share|web):[A-Za-z0-9_-]{1,120}$/;
export const isSkillKey = (v: unknown): v is string => typeof v === "string" && KEY.test(v);

export function useScore(u: SkillUses | undefined | null): number {
  if (!u) return 0;
  return u.saves * 5 + u.pulls * 3 + u.copies * 2 + u.opens;
}

/** "1.2k" — the way a count reads on a card. */
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
}

/** count one use. Fail-soft: a count that cannot be written never costs the
 *  thing the person was doing. */
export async function recordUse(db: SupabaseClient | null, key: string, kind: UseKind): Promise<boolean> {
  if (!db || !isSkillKey(key)) return false;
  try {
    const { error } = await db.rpc("community_skill_use", { p_key: key, p_kind: kind });
    return !error;
  } catch {
    return false;
  }
}

/** every count there is, by key. Fail-soft: a database that has not met 109
 *  reads as "nothing counted yet", and the shelf sorts by date. */
export async function allUses(db: SupabaseClient | null): Promise<Map<string, SkillUses>> {
  const out = new Map<string, SkillUses>();
  if (!db) return out;
  try {
    const { data } = await db.from("community_skill_uses").select("skill_key, copies, saves, pulls, opens").limit(5000);
    for (const r of (data ?? []) as ({ skill_key: string } & SkillUses)[]) {
      out.set(r.skill_key, { copies: Number(r.copies), saves: Number(r.saves), pulls: Number(r.pulls), opens: Number(r.opens) });
    }
  } catch {
    /* not counted yet */
  }
  return out;
}
